import { Component } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { MainState } from "@app/store/state";
import { SearchFilterCategory } from "@core/interfaces/search-filter-component.interface";
import { AstroUtilsService } from "@core/services/astro-utils/astro-utils.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { SearchBaseFilterComponent } from "@features/search/components/filters/search-base-filter/search-base-filter.component";
import { SearchAutoCompleteType } from "@features/search/enums/search-auto-complete-type.enum";
import { SearchFilterService } from "@features/search/services/search-filter.service";
import { PayableProductInterface } from "@features/subscriptions/interfaces/payable-product.interface";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "astrobin-coords-filter.search-filter-component",
  templateUrl: "../search-base-filter/search-base-filter.component.html",
  styleUrls: ["../search-base-filter/search-base-filter.component.scss"]
})
export class SearchCoordsFilterComponent extends SearchBaseFilterComponent {
  static key = SearchAutoCompleteType.COORDS;
  static minimumSubscription = PayableProductInterface.ULTIMATE;

  readonly category = SearchFilterCategory.SKY_AND_SUBJECTS;
  readonly label = this.searchFilterService.humanizeSearchAutoCompleteType(
    SearchCoordsFilterComponent.key as SearchAutoCompleteType
  );
  readonly raLabel = this.translateService.instant("Right ascension");
  readonly decLabel = this.translateService.instant("Declination");

  private _isUpdating = false;

  editFields = [
    {
      key: SearchCoordsFilterComponent.key,
      fieldGroup: [
        {
          key: "mode",
          type: "radio",
          wrappers: ["default-wrapper"],
          defaultValue: "boundingBox",
          props: {
            label: this.translateService.instant("Search mode"),
            formCheck: "inline",
            required: true,
            hideRequiredMarker: true,
            labelClassName: "me-3",
            options: [
              {
                value: "boundingBox",
                label: this.translateService.instant("Bounding box")
              },
              {
                value: "centerRadius",
                label: this.translateService.instant("Center + radius")
              }
            ]
          }
        },
        {
          type: "formly-template",
          hideExpression: model => !model || model.mode !== "boundingBox",
          template: `<div class="alert alert-info mb-3">
            ${this.translateService.instant("Find images whose field of view overlaps with the specified rectangular region. This returns all images that touch the area, even if only partially, which may include wide-field images where your target appears at the edge.")}
          </div>`
        },
        {
          type: "formly-template",
          hideExpression: model => !model || model.mode !== "centerRadius",
          template: `<div class="alert alert-info mb-3">
            ${this.translateService.instant("Find images whose field center is within the specified distance from your coordinates. This filters by where the image is actually centered, reducing unwanted results from wide-field shots that only marginally include the region.")}
          </div>`
        },
        {
          key: "ra",
          type: "slider",
          wrappers: ["default-wrapper"],
          hideExpression: model => !model || model.mode !== "boundingBox",
          props: {
            label: this.raLabel,
            required: true,
            hideRequiredMarker: true,
            showInputs: false,
            sliderOptions: {
              floor: 0,
              ceil: 1440, // 24 hours * 60 minutes
              step: 1,
              translate: (value: number): string => this.astroUtilsService.formatRa(value)
            }
          },
          hooks: {
            onInit: (field: FormlyFieldConfig) => {
              // Set default value
              if (!field.formControl.value) {
                field.formControl.setValue({ min: 0, max: 1440 });
              }

              // Function to update formatted input fields
              const updateFormattedFields = (value: any) => {
                if (!value) {
                  return;
                }

                const minHours = Math.floor(value.min / 60);
                const minMins = Math.round(value.min % 60);
                const maxHours = Math.floor(value.max / 60);
                const maxMins = Math.round(value.max % 60);

                // Find and update the formatted input fields
                const parent = field.parent;
                const raInputGroup = parent.fieldGroup.find(
                  g =>
                    g.fieldGroupClassName === "row" &&
                    g.fieldGroup?.some(f => f.key === "raMinFormatted" || f.key === "raMaxFormatted")
                );

                if (raInputGroup?.fieldGroup) {
                  const raMinField = raInputGroup.fieldGroup.find(f => f.key === "raMinFormatted");
                  const raMaxField = raInputGroup.fieldGroup.find(f => f.key === "raMaxFormatted");

                  const minFormatted = `${minHours}h ${minMins}m`;
                  const maxFormatted = `${maxHours}h ${maxMins}m`;

                  // Update both fields
                  if (raMinField?.formControl) {
                    raMinField.formControl.setValue(minFormatted, { emitEvent: false });
                  }

                  if (raMaxField?.formControl) {
                    raMaxField.formControl.setValue(maxFormatted, { emitEvent: false });
                  }
                }
              };

              // Initial sync - update formatted fields with current value
              if (field.formControl.value) {
                this.utilsService.delay(0).subscribe(() => {
                  updateFormattedFields(field.formControl.value);
                });
              }

              // Watch slider changes and update input fields
              field.formControl.valueChanges.subscribe(value => {
                if (!this._isUpdating && value) {
                  this._isUpdating = true;
                  updateFormattedFields(value);
                  this.utilsService.delay(0).subscribe(() => {
                    this._isUpdating = false;
                  });
                }
              });
            }
          }
        },
        {
          fieldGroupClassName: "row",
          hideExpression: model => !model || model.mode !== "boundingBox",
          fieldGroup: [
            {
              className: "col-6 px-2",
              key: "raMinFormatted",
              type: "input",
              wrappers: ["default-wrapper"],
              defaultValue: "0h 0m",
              props: {
                label: this.translateService.instant("Min"),
                type: "text",
                placeholder: "0h 0m",
                required: true,
                hideRequiredMarker: true,
                blur: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateRaFromFormatted(field, "min");
                  }
                },
                change: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateRaFromFormatted(field, "min");
                  }
                }
              }
            },
            {
              className: "col-6 px-2",
              key: "raMaxFormatted",
              type: "input",
              wrappers: ["default-wrapper"],
              defaultValue: "24h 0m",
              props: {
                label: this.translateService.instant("Max"),
                type: "text",
                placeholder: "24h 0m",
                required: true,
                hideRequiredMarker: true,
                blur: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateRaFromFormatted(field, "max");
                  }
                },
                change: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateRaFromFormatted(field, "max");
                  }
                }
              }
            }
          ]
        },
        {
          key: "dec",
          type: "slider",
          wrappers: ["default-wrapper"],
          hideExpression: model => !model || model.mode !== "boundingBox",
          props: {
            label: this.decLabel,
            required: true,
            hideRequiredMarker: true,
            showInputs: false,
            sliderOptions: {
              floor: -90,
              ceil: 90,
              step: 0.1,
              translate: (value: number): string => this.astroUtilsService.formatDec(value)
            }
          },
          hooks: {
            onInit: (field: FormlyFieldConfig) => {
              // Set default value
              if (!field.formControl.value) {
                field.formControl.setValue({ min: -90, max: 90 });
              }

              // Function to update formatted input fields
              const updateFormattedFields = (value: any) => {
                if (!value) {
                  return;
                }

                const minDegrees = Math.trunc(value.min);
                const minMinutes = Math.round(Math.abs((value.min - minDegrees) * 60));
                const maxDegrees = Math.trunc(value.max);
                const maxMinutes = Math.round(Math.abs((value.max - maxDegrees) * 60));

                // Find and update the formatted input fields
                const parent = field.parent;
                const decInputGroup = parent.fieldGroup.find(
                  g =>
                    g.fieldGroupClassName === "row" &&
                    g.fieldGroup?.some(f => f.key === "decMinFormatted" || f.key === "decMaxFormatted")
                );

                if (decInputGroup?.fieldGroup) {
                  const decMinField = decInputGroup.fieldGroup.find(f => f.key === "decMinFormatted");
                  const decMaxField = decInputGroup.fieldGroup.find(f => f.key === "decMaxFormatted");

                  const minFormatted = `${minDegrees}° ${minMinutes}'`;
                  const maxFormatted = `${maxDegrees}° ${maxMinutes}'`;

                  // Update both fields
                  if (decMinField?.formControl) {
                    decMinField.formControl.setValue(minFormatted, { emitEvent: false });
                  }

                  if (decMaxField?.formControl) {
                    decMaxField.formControl.setValue(maxFormatted, { emitEvent: false });
                  }
                }
              };

              // Initial sync - update formatted fields with current value
              if (field.formControl.value) {
                this.utilsService.delay(0).subscribe(() => {
                  updateFormattedFields(field.formControl.value);
                });
              }

              // Watch slider changes and update input fields
              field.formControl.valueChanges.subscribe(value => {
                if (!this._isUpdating && value) {
                  this._isUpdating = true;
                  updateFormattedFields(value);
                  this.utilsService.delay(0).subscribe(() => {
                    this._isUpdating = false;
                  });
                }
              });
            }
          }
        },
        {
          fieldGroupClassName: "row",
          hideExpression: model => !model || model.mode !== "boundingBox",
          fieldGroup: [
            {
              className: "col-6 px-2",
              key: "decMinFormatted",
              type: "input",
              wrappers: ["default-wrapper"],
              defaultValue: "-90° 0'",
              props: {
                label: this.translateService.instant("Min"),
                type: "text",
                placeholder: "-90° 0'",
                required: true,
                hideRequiredMarker: true,
                blur: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateDecFromFormatted(field, "min");
                  }
                },
                change: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateDecFromFormatted(field, "min");
                  }
                }
              }
            },
            {
              className: "col-6 px-2",
              key: "decMaxFormatted",
              type: "input",
              wrappers: ["default-wrapper"],
              defaultValue: "90° 0'",
              props: {
                label: this.translateService.instant("Max"),
                type: "text",
                placeholder: "90° 0'",
                required: true,
                hideRequiredMarker: true,
                blur: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateDecFromFormatted(field, "max");
                  }
                },
                change: field => {
                  if (!this._isUpdating) {
                    this.parseAndUpdateDecFromFormatted(field, "max");
                  }
                }
              }
            }
          ]
        },
        {
          key: "raCenterInput",
          type: "input",
          wrappers: ["default-wrapper"],
          hideExpression: model => !model || model.mode !== "centerRadius",
          props: {
            label: this.translateService.instant("RA center"),
            type: "text",
            placeholder: "12h 30m or 187.5",
            required: true,
            hideRequiredMarker: true,
            description: this.translateService.instant(
              "Enter as hours/minutes/seconds (e.g. 12h 30m 15s) or decimal degrees (e.g. 187.5)"
            )
          }
        },
        {
          key: "decCenterInput",
          type: "input",
          wrappers: ["default-wrapper"],
          hideExpression: model => !model || model.mode !== "centerRadius",
          props: {
            label: this.translateService.instant("Dec center"),
            type: "text",
            placeholder: "-45° 30' or -45.5",
            required: true,
            hideRequiredMarker: true,
            description: this.translateService.instant(
              "Enter as degrees/arcminutes/arcseconds (e.g. -45° 30' 15\") or decimal degrees (e.g. -45.5)"
            )
          }
        },
        {
          key: "radius",
          type: "input",
          wrappers: ["default-wrapper"],
          defaultValue: 1,
          hideExpression: model => !model || model.mode !== "centerRadius",
          props: {
            label: this.translateService.instant("Search radius (degrees)"),
            type: "number",
            step: 0.1,
            min: 0.1,
            max: 180,
            required: true,
            hideRequiredMarker: true,
            description: this.translateService.instant("Search radius in decimal degrees")
          }
        }
      ],
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          if (!this.editModel[SearchCoordsFilterComponent.key]) {
            field.formControl.patchValue({
              mode: "boundingBox",
              ra: { min: 0, max: 1440 },
              dec: { min: -90, max: 90 },
              raMinFormatted: "0h 0m",
              raMaxFormatted: "24h 0m",
              decMinFormatted: "-90° 0'",
              decMaxFormatted: "90° 0'",
              raCenterInput: "0",
              decCenterInput: "0",
              radius: 1
            });
          } else {
            // Set the mode based on existing value
            const currentValue = this.editModel[SearchCoordsFilterComponent.key];

            // Determine mode from existing data
            const mode = currentValue.raCenter !== undefined ? "centerRadius" : "boundingBox";

            if (mode === "centerRadius" && currentValue.raCenter !== undefined) {
              // Set mode first to make fields visible
              field.formControl.patchValue({
                mode: "centerRadius"
              });

              // Then set the values after a small delay to ensure fields are rendered
              this.utilsService.delay(0).subscribe(() => {
                // Format raCenter from minutes to HMS for display
                const raFormatted = this.astroUtilsService.formatRa(currentValue.raCenter);
                // Format decCenter from decimal degrees to DMS for display
                const decFormatted = this.astroUtilsService.formatDec(currentValue.decCenter);
                field.formControl.patchValue({
                  raCenterInput: raFormatted,
                  decCenterInput: decFormatted,
                  radius: currentValue.radius
                });
              });
            } else {
              // Bounding box mode
              field.formControl.patchValue({
                mode: "boundingBox"
              });
            }
          }
        }
      }
    }
  ];

  constructor(
    public readonly store$: Store<MainState>,
    public readonly translateService: TranslateService,
    public readonly domSanitizer: DomSanitizer,
    public readonly modalService: NgbModal,
    public readonly searchFilterService: SearchFilterService,
    public readonly astroUtilsService: AstroUtilsService,
    public readonly utilsService: UtilsService
  ) {
    super(store$, translateService, domSanitizer, modalService, searchFilterService);
  }

  readonly valueTransformer = (value: any) => {
    if (!value || !value.mode) {
      return value;
    }

    if (value.mode === "centerRadius") {
      // Parse raCenterInput to get minutes (0-1440)
      let raCenter: number;
      const raCenterInput = (value.raCenterInput || "").trim();
      const hmsMatch = raCenterInput.match(/^(\d+(?:\.\d+)?)\s*h?\s*(\d+(?:\.\d+)?)?\s*m?\s*(\d+(?:\.\d+)?)?\s*s?$/);

      if (hmsMatch && (raCenterInput.includes("h") || raCenterInput.includes("m") || raCenterInput.includes("s"))) {
        // Parse as hours/minutes/seconds and convert to minutes
        const hours = parseFloat(hmsMatch[1]) || 0;
        const minutes = parseFloat(hmsMatch[2]) || 0;
        const seconds = parseFloat(hmsMatch[3]) || 0;
        raCenter = hours * 60 + minutes + seconds / 60;
      } else {
        // Parse as decimal degrees and convert to minutes
        const degrees = parseFloat(raCenterInput) || 0;
        raCenter = degrees * 4; // 360 degrees = 1440 minutes, so 1 degree = 4 minutes
      }

      // Parse decCenterInput to get degrees
      let decCenter: number;
      const decCenterInput = (value.decCenterInput || "").trim();
      const dmsMatch = decCenterInput.match(/^(-?\d+(?:\.\d+)?)\s*°?\s*(\d+(?:\.\d+)?)?\s*'?\s*(\d+(?:\.\d+)?)?\s*"?$/);

      if (dmsMatch && (decCenterInput.includes("°") || decCenterInput.includes("'") || decCenterInput.includes('"'))) {
        // Parse as degrees/arcminutes/arcseconds
        const degrees = parseFloat(dmsMatch[1]) || 0;
        const arcminutes = parseFloat(dmsMatch[2]) || 0;
        const arcseconds = parseFloat(dmsMatch[3]) || 0;
        const sign = degrees < 0 ? -1 : 1;
        decCenter = degrees + sign * (arcminutes / 60 + arcseconds / 3600);
      } else {
        // Parse as decimal degrees
        decCenter = parseFloat(decCenterInput) || 0;
      }

      // Return center + radius format (raCenter in minutes, 0-1440)
      return {
        raCenter: Math.min(1440, Math.max(0, raCenter)),
        decCenter: Math.min(90, Math.max(-90, decCenter)),
        radius: parseFloat(value.radius)
      };
    } else {
      // Return bounding box format (RA in minutes, as on master)
      return {
        ra: {
          min: value.ra?.min || 0,
          max: value.ra?.max || 1440
        },
        dec: {
          min: value.dec?.min || -90,
          max: value.dec?.max || 90
        }
      };
    }
  };

  render(): SafeHtml {
    if (!this.value) {
      return "";
    }

    if (this.value.raCenter !== undefined) {
      // Render center + radius format (raCenter is in minutes)
      const raFormatted = this.astroUtilsService.formatRa(this.value.raCenter);
      const decFormatted = this.astroUtilsService.formatDec(this.value.decCenter);
      const radiusFormatted = this.value.radius.toFixed(2);

      return this.domSanitizer.bypassSecurityTrustHtml(
        `${this.translateService.instant("Center")}: RA ${raFormatted}, Dec ${decFormatted}, ${this.translateService.instant("Radius")}: ${radiusFormatted}°`
      );
    } else {
      // Render bounding box format (RA is in minutes)
      const minRa = this.astroUtilsService.formatRa(this.value.ra.min);
      const maxRa = this.astroUtilsService.formatRa(this.value.ra.max);
      const minDec = this.astroUtilsService.formatDec(this.value.dec.min);
      const maxDec = this.astroUtilsService.formatDec(this.value.dec.max);

      return this.domSanitizer.bypassSecurityTrustHtml(`RA: ${minRa} - ${maxRa}, Dec: ${minDec} - ${maxDec}`);
    }
  }

  private parseAndUpdateRaFromFormatted(field: FormlyFieldConfig, type: "min" | "max"): void {
    this._isUpdating = true;

    const value = field.formControl.value || "";
    // Parse formatted RA value (e.g., "12h 34m" or "12h34m" or "12 34")
    const match = value.match(/(\d+)\s*h?\s*(\d+)\s*m?/);

    if (match) {
      const hours = parseInt(match[1], 10) || 0;
      const minutes = parseInt(match[2], 10) || 0;
      const totalMinutes = Math.min(1440, Math.max(0, hours * 60 + minutes));

      // Format the value properly
      const formattedValue = `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
      field.formControl.setValue(formattedValue, { emitEvent: false });

      // Update the slider
      const parent = field.parent.parent;
      const raSlider = parent.fieldGroup.find(f => f.key === "ra");

      if (raSlider && raSlider.formControl) {
        const currentValue = raSlider.formControl.value || { min: 0, max: 1440 };
        if (type === "min") {
          raSlider.formControl.setValue({ min: totalMinutes, max: currentValue.max });
        } else {
          raSlider.formControl.setValue({ min: currentValue.min, max: totalMinutes });
        }
      }
    }

    this.utilsService.delay(0).subscribe(() => {
      this._isUpdating = false;
    });
  }

  private parseAndUpdateDecFromFormatted(field: FormlyFieldConfig, type: "min" | "max"): void {
    this._isUpdating = true;

    const value = field.formControl.value || "";
    // Parse formatted Dec value (e.g., "-45° 30'" or "-45°30'" or "-45 30")
    const match = value.match(/(-?\d+)\s*°?\s*(\d+)\s*'?/);

    if (match) {
      const degrees = parseInt(match[1], 10) || 0;
      const minutes = parseInt(match[2], 10) || 0;
      const sign = degrees < 0 ? -1 : 1;
      const decValue = Math.min(90, Math.max(-90, degrees + (sign * minutes) / 60));

      // Format the value properly
      const formattedDegrees = Math.trunc(decValue);
      const formattedMinutes = Math.round(Math.abs((decValue - formattedDegrees) * 60));
      const formattedValue = `${formattedDegrees}° ${formattedMinutes}'`;
      field.formControl.setValue(formattedValue, { emitEvent: false });

      // Update the slider
      const parent = field.parent.parent;
      const decSlider = parent.fieldGroup.find(f => f.key === "dec");

      if (decSlider && decSlider.formControl) {
        const currentValue = decSlider.formControl.value || { min: -90, max: 90 };
        if (type === "min") {
          decSlider.formControl.setValue({ min: decValue, max: currentValue.max });
        } else {
          decSlider.formControl.setValue({ min: currentValue.min, max: decValue });
        }
      }
    }

    this.utilsService.delay(0).subscribe(() => {
      this._isUpdating = false;
    });
  }
}
